import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { EffectiveFileType } from './file-type';

export type AGUFileDropRejection = { Reason: 'type' | 'size'; FileName: string };

let nextFileDropID = 0;

/**
 * Single-file drop/pick box (OEP prototype markup.html:339-342: dashed box, title line, note line).
 * Validation only; uploading is the host's job. The box is a real button that opens a hidden native
 * file input, so Enter/Space work without custom key handling (UBP §4). A visible button-styled
 * label (ButtonLabel) shows what the panel does; clicking while Disabled emits (BlockedClick) so the
 * host can explain why. Rejection copy and any aria-live announcement belong to the host, which
 * receives (Rejected).
 */
@Component({
    standalone: false,
    selector: 'agu-file-drop',
    templateUrl: './file-drop.component.html',
    styleUrl: './file-drop.component.scss',
})
export class AGUFileDropComponent {
    @Input({ required: true }) Title!: string;
    @Input() Note: string | null = null;
    @Input() Hint: string | null = null;
    @Input() ButtonLabel = 'Choose file';
    /** Allowed MIME types; empty = any. */
    @Input() Accept: readonly string[] = [];
    @Input() MaxBytes = Number.POSITIVE_INFINITY;
    @Input() Disabled = false;
    @Input() Busy = false;
    /**
     * `'bare'` drops the OEP box: no border, ground or padding, so a host that draws its own box (Career Explorer's `.cx-drop`,
     * P/06_career-explorer.html:316-324) owns the frame. Order is title, hint, note, projected content (the consent line),
     * then the button; the button, not the whole area, opens the picker. Drag and drop still land on the area.
     */
    @Input() Variant: 'box' | 'bare' = 'box';
    @Output() FileChosen = new EventEmitter<File>();
    @Output() Rejected = new EventEmitter<AGUFileDropRejection>();
    @Output() BlockedClick = new EventEmitter<void>();
    @ViewChild('picker') private picker?: ElementRef<HTMLInputElement>;

    DragOver = false;
    readonly NoteID = `agu-file-drop-note-${nextFileDropID++}`;

    get AcceptAttr(): string { return this.Accept.join(','); }

    Open(): void {
        if (this.Busy) return;
        if (this.Disabled) { this.BlockedClick.emit(); return; }
        this.picker?.nativeElement.click();
    }

    Consider(f: File): void {
        if (this.Disabled || this.Busy) return;
        if (this.Accept.length > 0 && !this.Accept.includes(EffectiveFileType(f))) {
            this.Rejected.emit({ Reason: 'type', FileName: f.name });
            return;
        }
        if (f.size > this.MaxBytes) {
            this.Rejected.emit({ Reason: 'size', FileName: f.name });
            return;
        }
        this.FileChosen.emit(f);
    }

    OnPick(event: Event): void {
        const input = event.target as HTMLInputElement;
        const f = input.files?.[0];
        input.value = ''; // choosing the same file twice must fire again
        if (f) this.Consider(f);
    }

    OnDragOver(e: DragEvent): void {
        e.preventDefault(); // always, so a rejected drop never navigates the browser to the file
        this.DragOver = !this.Disabled && !this.Busy;
    }
    OnDragLeave(): void { this.DragOver = false; }
    OnDrop(e: DragEvent): void {
        e.preventDefault();
        this.DragOver = false;
        const f = e.dataTransfer?.files?.[0];
        if (f) this.Consider(f);
    }
}
